import React, { useState, useEffect } from "react";
import {
  Fieldset,
  Legend,
  Field,
  Label,
  Description,
  ErrorMessage,
} from "@/components/fieldset";
import { Input } from "@/components/input";
import { Select } from "@/components/select";
import { Checkbox } from "@/components/checkbox";
import { Button } from "@/components/button";
import type { ICreateStudentRequest } from "@/apis/users/createUser";
import { useQueryGetGroupList } from "@/apis/groups/getGroupList";

interface StudentFormProps {
  initialData?: Partial<ICreateStudentRequest>;
  onSubmit: (data: ICreateStudentRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
  serverError?: string;
  isEditMode?: boolean;
}

const StudentForm: React.FC<StudentFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  serverError,
  isEditMode = false,
}) => {
  // 그룹 목록 조회
  const { data: groupListData } = useQueryGetGroupList();
  const groupList = groupListData?.items || [];

  // 학생 정보 상태 관리
  const [formData, setFormData] = useState<ICreateStudentRequest>({
    email: "",
    password: "",
    name: "",
    grade: 0,
    is_graduated: false,
    school: "",
    phone: "",
    parent_phone: "",
    memo: "",
    group_id: "",
    ...initialData,
  });

  // 유효성 검사 오류 상태
  const [errors, setErrors] = useState<Record<string, string>>({});

  // initialData가 변경될 때 폼 데이터 업데이트
  useEffect(() => {
    if (initialData) {
      // 얕은 비교를 통해 initialData가 실제로 바뀌었을 때만 setFormData 호출
      const hasChanged = Object.keys(initialData).some(
        (key) =>
          initialData[key as keyof typeof initialData] !==
          formData[key as keyof typeof formData]
      );
      if (hasChanged) {
        setFormData((prev) => ({ ...prev, ...initialData }));
      }
    }
  }, [initialData]);

  // 입력 필드 변경 핸들러
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    // 체크박스인 경우 checked 속성 사용
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    }
    // grade 필드는 숫자로 변환
    else if (name === "grade") {
      setFormData((prev) => ({ ...prev, [name]: parseInt(value, 10) }));
    }
    // 숫자 타입인 경우 숫자로 변환
    else if (type === "number") {
      setFormData((prev) => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : 0,
      }));
    }
    // 일반 텍스트 입력
    else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // 필드가 변경되면 해당 필드의 오류 메시지 제거
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // 체크박스 변경 핸들러
  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  // 폼 제출 핸들러
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검사
    const validationErrors: Record<string, string> = {};

    if (!formData.email) {
      validationErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      validationErrors.email = "Email is invalid";
    }

    // 비밀번호는 수정 모드에서는 필수가 아님
    if (!isEditMode) {
      if (!formData.password) {
        validationErrors.password = "Password is required";
      } else if (formData.password.length < 6) {
        validationErrors.password = "Password must be at least 6 characters";
      }
    } else if (formData.password && formData.password.length < 6) {
      validationErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.name) {
      validationErrors.name = "Name is required";
    }

    // 유효성 검사 오류가 있으면 제출하지 않음
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // 최종 제출 데이터 준비 (grade가 숫자인지 확인)
    const submitData = {
      ...formData,
      grade:
        typeof formData.grade === "string"
          ? parseInt(formData.grade, 10)
          : formData.grade,
    };

    // 수정 모드에서 빈 비밀번호는 제거 (서버에서 업데이트하지 않도록)
    if (isEditMode && !submitData.password) {
      const { password, ...dataWithoutPassword } = submitData;
      onSubmit(dataWithoutPassword as ICreateStudentRequest);
    } else {
      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" autoComplete="off">
      {/* 서버 오류 표시 */}
      {serverError && (
        <div className="p-4 bg-red-50 text-red-600 rounded-md">
          {serverError}
        </div>
      )}

      {/* 기본 정보 */}
      <Fieldset>
        <Legend>Basic Information</Legend>

        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field>
            <Label htmlFor="email">Email*</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="student@example.com"
              value={formData.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              autoComplete="new-email"
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </Field>

          <Field>
            <Label htmlFor="password">
              {isEditMode
                ? "Password (Leave empty to keep unchanged)"
                : "Password*"}
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder={isEditMode ? "Enter new password" : "Enter password"}
              value={formData.password}
              onChange={handleChange}
              aria-invalid={!!errors.password}
              autoComplete="new-password"
            />
            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
            <Description>Password must be at least 6 characters.</Description>
          </Field>

          <Field>
            <Label htmlFor="name">Name*</Label>
            <Input
              id="name"
              name="name"
              placeholder="Enter student name"
              value={formData.name}
              onChange={handleChange}
              aria-invalid={!!errors.name}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </Field>

          <Field>
            <Label htmlFor="school">School</Label>
            <Input
              id="school"
              name="school"
              placeholder="School name"
              value={formData.school || ""}
              onChange={handleChange}
            />
          </Field>

          <Field>
            <Label htmlFor="group_id">Group</Label>
            <Select
              id="group_id"
              name="group_id"
              value={formData.group_id || ""}
              onChange={handleChange}
            >
              <option value="">No Group</option>
              {groupList.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
            <Description>
              Select a group to assign this student to a specific group.
            </Description>
          </Field>

          <Field>
            <Label htmlFor="grade">Grade</Label>
            <Select
              id="grade"
              name="grade"
              value={formData.grade?.toString() || "0"}
              onChange={handleChange}
            >
              <option value="0">Not specified</option>
              <option value="1">Grade 1</option>
              <option value="2">Grade 2</option>
              <option value="3">Grade 3</option>
              <option value="4">Grade 4</option>
              <option value="5">Grade 5</option>
              <option value="6">Grade 6</option>
              <option value="7">Grade 7</option>
              <option value="8">Grade 8</option>
              <option value="9">Grade 9</option>
              <option value="10">Grade 10</option>
              <option value="11">Grade 11</option>
              <option value="12">Grade 12</option>
            </Select>
          </Field>

          <Field className="flex items-center space-x-2">
            <div className="flex items-center space-x-2 mt-6 ">
              <Checkbox
                id="is_graduated"
                checked={formData.is_graduated}
                onChange={(checked) =>
                  handleCheckboxChange("is_graduated", checked)
                }
              />
              <Label htmlFor="is_graduated">Graduated</Label>
            </div>
          </Field>
        </div>
      </Fieldset>

      {/* 연락처 정보 */}
      <Fieldset>
        <Legend>Contact Information</Legend>

        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field>
            <Label htmlFor="phone">Student Phone</Label>
            <Input
              id="phone"
              name="phone"
              placeholder="010-1234-5678"
              value={formData.phone || ""}
              onChange={handleChange}
              autoComplete="off"
            />
          </Field>

          <Field>
            <Label htmlFor="parent_phone">Parent Phone</Label>
            <Input
              id="parent_phone"
              name="parent_phone"
              placeholder="010-8765-4321"
              value={formData.parent_phone || ""}
              onChange={handleChange}
              autoComplete="off"
            />
          </Field>
        </div>
      </Fieldset>

      {/* 추가 정보 */}
      <Fieldset>
        <Legend>Additional Information</Legend>

        <div className="mt-4">
          <Field>
            <Label htmlFor="memo">Memo</Label>
            <textarea
              id="memo"
              name="memo"
              rows={3}
              className="w-full rounded-lg border border-zinc-950/10 px-3.5 py-2 text-base/6 text-zinc-950 shadow-sm sm:px-3 sm:py-1.5 sm:text-sm/6 dark:border-white/10 dark:bg-white/5 dark:text-white"
              placeholder="Additional notes..."
              value={formData.memo || ""}
              onChange={handleChange}
            />
          </Field>
        </div>
      </Fieldset>

      {/* 제출 버튼 */}
      <div className="flex justify-end space-x-4">
        <Button plain onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? "Submitting..."
            : isEditMode
            ? "Update Student"
            : "Create Student"}
        </Button>
      </div>
    </form>
  );
};

export default StudentForm;
